import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {AgGridReact} from 'ag-grid-react';
import BulkActionBar from './bulkActionBar/bulkActionBar.component';
import {Pagination} from '../../components';
import {throttle} from 'lodash';
import {getDefaultRowClassRules, getDefaultExcelStyles, getClassName} from '../../utils';

import 'ag-grid-community/dist/styles/ag-grid.css';
import './agGrid.component.scss';

/**
 * AgGrid Wrapping Component
 */
class AgGrid extends PureComponent {

  /**
   * Constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      bulkActionBar: {
        isActive: this.getIsBulkActionBarActive(props.columnDefs, props.autoGroupColumnDef),
        isVisible: false,
        isBulkSelected: false
      }
    };

    this.handleBulkActionBarUpdate = throttle(this.handleBulkActionBarUpdate, 50);
  }

  /**
   * ComponentDidUpdateGridOptions
   */
  componentDidUpdate(prevProps) {
    const {columnDefs} = this.props;

    if ((columnDefs && columnDefs.length) !== (prevProps.columnDefs && prevProps.columnDefs.length)) {
      const {bulkActionBar} = this.state;

      this.setState({
        bulkActionBar: {
          ...bulkActionBar,
          isActive: this.getIsBulkActionBarActive(columnDefs)
        }
      });
    }
  }


  /**
   * Ag-grid callback wrapping function to provide utility and convenient auto-size functionality
   * @param grid
   */
  handleGridCallback = (grid) => {
    if (!grid) {
      return;
    }

    switch (grid.type) {
      case 'gridReady':
        const {sizeToFit, onGridReady} = this.props;

        this.agGrid = grid;

        if (sizeToFit) {
          grid.api.sizeColumnsToFit();
        }

        onGridReady && onGridReady(grid);
        break;
      case 'rowSelected':
        const {onRowSelected} = this.props;

        this.handleBulkActionBarUpdate(grid);
        onRowSelected && onRowSelected(grid);
        break;
      case 'rowDataChanged':
        const {onRowDataChanged} = this.props;

        this.handleBulkActionBarUpdate(grid);
        onRowDataChanged && onRowDataChanged(grid);
        break;
      default:
        break;
    }
  };

  /**
   * Handle updating of bulkActionBar state
   */
  handleBulkActionBarUpdate = (grid) => {
    const {rowData} = this.props;
    const {bulkActionBar} = this.state;

    if (bulkActionBar.isActive) {
      const selectedRows = grid.api.getSelectedRows();
      const isVisible = selectedRows && selectedRows.length > 0;
      const isBulkSelected = selectedRows && rowData && selectedRows.length === rowData.length;

      this.setState({
        bulkActionBar: {
          ...bulkActionBar,
          isVisible,
          isBulkSelected
        }
      });
    }
  };

  /**
   * Handle onChange for BulkActionBar checkbox
   * @param value
   */
  handleBulkCheckboxChange = (value) => {
    if (!this.agGrid) {
      return;
    }

    if (value) {
      this.agGrid.api.selectAll();
    } else {
      this.agGrid.api.deselectAll();
    }
  };

  /**
   * Get isBulkActionBarActive value
   * @param columnDefs
   * @param autoGroupColumnDef
   * @returns {boolean}
   */
  getIsBulkActionBarActive = (columnDefs, autoGroupColumnDef) => {
    if (!columnDefs || (!columnDefs.length && !autoGroupColumnDef)) {
      return false;
    }

    return !!(
        (columnDefs || []).find((col) => col.headerCheckboxSelection) ||
        (autoGroupColumnDef && autoGroupColumnDef.headerCheckboxSelection)
    );
  };

  /**
   * Get Q4 Icons mapped to the grid icons
   * @returns {*}
   */
  getIcons = () => {
    return {
      menu: '<i class="q4i-hamburger-q4inc-4pt"/>',
      sortAscending: '<i class="q4i-arrow-sm-up-4pt"/>',
      sortDescending: '<i class="q4i-arrow-sm-down-4pt"/>',
      columnMoveHide: '<i class="q4i-close-4pt"/>',
      columnMoveMove: '<i class="q4i-swap-4pt"/>',
      columnMoveLeft: '<i class="q4i-swap-4pt"/>',
      columnMoveRight: '<i class="q4i-swap-4pt"/>',
      columnGroupOpened: '<i class="q4i-caret-sm-up-4pt"/>',
      columnGroupClosed: '<i class="q4i-caret-sm-down-4pt"/>',
      groupExpanded: '<i class="q4i-caret-sm-up-4pt"/>',
      groupContracted: '<i class="q4i-caret-sm-down-4pt"/>'
    };
  };

  /**
   * Render BulkActionBar component
   * @returns {*}
   */
  renderBulkActionBar = () => {
    const {bulkActions, rowData} = this.props;
    const {bulkActionBar} = this.state;
    const {isActive, isVisible, isBulkSelected} = bulkActionBar;

    if (!isActive || !bulkActions || !bulkActions.length || !rowData || !rowData.length || !this.agGrid) {
      return null;
    }

    return (
        <BulkActionBar
            isVisible={isVisible}
            isBulkSelected={isBulkSelected}
            actions={bulkActions}
            onCheckboxChange={this.handleBulkCheckboxChange}
        />
    );
  };

  /**
   * Render
   * @return {XML}
   */
  render() {
    const {
      className, domLayout, width, height, rowDensity, isPivot,
      rowData, pagination, paginationProps,
      onRowClicked, isPinned
    } = this.props;

    const noData = !rowData || !rowData.length;
    const baseClassName = getClassName('ag-grid-root ag-theme-bootstrap', [
      {condition: className, trueClassName: className},
      {condition: domLayout === 'autoHeight', trueClassName: 'ag-grid-root--auto-height'},
      {condition: pagination, trueClassName: 'ag-grid-root--paginated'},
      {condition: noData, trueClassName: 'ag-grid-root--no-data'},
      {condition: rowDensity, trueClassName: `ag-grid-root--${rowDensity}`},
      {condition: isPivot, trueClassName: 'ag-grid-root--pivot'},
      {condition: isPinned, trueClassName: 'ag-grid-root--pinned'}
    ]);

    const baseStyle = {
      width,
      height: domLayout === 'autoHeight' ? null : height
    };

    const rowHeight = {
      'default': 50,
      'comfy': 40,
      'compact': 26
    }[rowDensity] || 50;

    return (
        <div className={baseClassName} style={baseStyle}>
          {this.renderBulkActionBar()}
          <AgGridReact
              headerHeight={50}
              rowHeight={rowHeight}

              sortingOrder={['asc', 'desc']}
              suppressMultiSort={true}

              pivotMode={isPivot}

              rowClass={onRowClicked && 'ag-row--clickable'}
              rowSelection='multiple'
              suppressRowClickSelection={true}

              rowClassRules={getDefaultRowClassRules()}
              excelStyles={getDefaultExcelStyles()}

              suppressCellSelection={true}
              ensureDomOrder={true}
              suppressRowTransform={true}
              suppressDragLeaveHidesColumns={true}
              getMainMenuItems={() => ['pinSubMenu']}
              getContextMenuItems={() => ['copy', 'copyWithHeaders']}

              icons={this.getIcons()}
              overlayNoRowsTemplate={'No data available'}

              {...this.props}

              // override callback for utility and convenient auto-sizing
              onGridReady={this.handleGridCallback}
              onRowSelected={this.handleGridCallback}
              onRowDataChanged={this.handleGridCallback}

              // override ag-grid pagination
              pagination={false}
              suppressPaginationPanel={true}
          />
          {pagination && !noData && (
              <Pagination
                  {...paginationProps}
              />
          )}
        </div>
    );
  }
}

AgGrid.propTypes = {
  /**
   * A custom className to add to the component
   */
  className: PropTypes.string,

  /**
   * Box model  / sizing props
   */
  domLayout: PropTypes.oneOf(['normal', 'autoHeight']),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Used to determine row height
   * Note: changing rowHeight in Ag-grid requires both JS and CSS, which is why this prop was introduced
   */
  rowDensity: PropTypes.oneOf(['default', 'comfy', 'compact']),

  /**
   * Used to provide the grid with columns and data
   */
  columnDefs: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    headerName: PropTypes.string,
    headerClass: PropTypes.oneOfType([PropTypes.func, PropTypes.array, PropTypes.string]),
    cellClassRules: PropTypes.object,
    valueFormatter: PropTypes.func,
    width: PropTypes.number,
    minWidth: PropTypes.number,
    sortable: PropTypes.bool,
    lockPinned: PropTypes.bool,
  })).isRequired,
  rowData: PropTypes.array.isRequired,

  /**
   * If using checkbox selection in the header, used to provide bulk action actions
   */
  bulkActions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    icon: PropTypes.string,
    label: PropTypes.string,
    onSelect: PropTypes.func.isRequired
  })),

  /**
   * Used to add pagination to the table
   */
  pagination: PropTypes.bool,
  paginationProps: PropTypes.shape({
    startFromZero: PropTypes.bool,
    total: PropTypes.number,
    initialPage: PropTypes.number,
    forcePage: PropTypes.number,
    initialPageSize: PropTypes.number,
    pageSizeOptions: PropTypes.array,
    pageSizeId: PropTypes.string,
    showPageSizeSelection: PropTypes.bool,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func
  }),

  /**
   * Used to have the grid attempt auto-sizing after firing onGridReady callback
   * Note: convenience function
   */
  sizeToFit: PropTypes.bool,

  /**
   * Some of the important ag-Grid specific props (there are many many many many more)
   * Learn more at https://www.ag-grid.com/javascript-grid-reference-overview/
   */
  onSortChanged: PropTypes.func,
  onRowDataChanged: PropTypes.func,
  onGridReady: PropTypes.func,
  onColumnMoved: PropTypes.func,
  onDragStopped: PropTypes.func,

  /**
   * Used to provide custom components to render cell
   * Learn more at: https://www.ag-grid.com/javascript-grid-cell-rendering-components/#react-cell-rendering
   */
  frameworkComponents: PropTypes.object,
};

AgGrid.defaultProps = {
  headerHeight: 50,
  rowDensity: 'default'
};

export default AgGrid;
